import { useState } from 'react';
import { mapi } from './api';
import { fmtDate, liikevaihto } from '../lib/format';
import {
  Button, ButtonRow, Chip, Empty, Hint, Muted, NeutralChip, Table, TableWrap, Warn,
} from '../ui/primitives';
import type { CustomerRow } from './types';
import type { SearchResult, SearchRun } from '../types';
import { Osio } from './Osio';

/* Hakuhistoria on ainoa paikka, josta näkee mitä liidihaku oikeasti tuotti ja
   mitä myyjä siitä otti. Se on hakumallin kehittämisen raaka-aine: AI:n oma
   perustelu kertoo mitä se luuli, tuotu/ei tuotu kertoo osuiko se oikeaan.
   Näkymä on vain luku — master ei koske asiakkaan liideihin. */

interface Props {
  asiakas: CustomerRow;
  onToast: (v: string) => void;
}

const SOPIVUUS = ['', 'Ei vastaa', 'Heikko', 'Mahdollinen', 'Hyvä', 'Täsmää'];

function Sopivuus({ arvo, syy }: { arvo?: number | null; syy?: string | null }) {
  if (!arvo || arvo < 1 || arvo > 5) return <Muted>—</Muted>;
  const vari = arvo >= 4 ? '#5C6B54' : arvo === 3 ? '#8A6A2F' : '#8A4B3C';
  return <Chip $bg={vari} title={syy || undefined}>{SOPIVUUS[arvo]}</Chip>;
}

export function SearchesCard({ asiakas, onToast }: Props) {
  const [auki, setAuki] = useState(false);
  const [runs, setRuns] = useState<SearchRun[] | null>(null);
  const [saatavilla, setSaatavilla] = useState(true);
  const [avattu, setAvattu] = useState<number | null>(null);
  const [rivit, setRivit] = useState<SearchResult[] | null>(null);

  const lataa = async () => {
    if (auki) { setAuki(false); return; }
    setAuki(true);
    try {
      const r = await mapi<{ saatavilla: boolean; runs: SearchRun[] }>(
        `/api/master/customers/${asiakas.id}/searches`);
      setSaatavilla(r.saatavilla);
      setRuns(r.runs);
    } catch (err) {
      setRuns([]);
      onToast(err instanceof Error ? err.message : String(err));
    }
  };

  const avaa = async (id: number) => {
    if (avattu === id) { setAvattu(null); setRivit(null); return; }
    setAvattu(id);
    setRivit(null);
    try {
      const r = await mapi<{ results: SearchResult[] }>(
        `/api/master/customers/${asiakas.id}/searches/${id}`);
      setRivit(r.results);
    } catch (err) {
      setRivit([]);
      onToast(err instanceof Error ? err.message : String(err));
    }
  };

  // Osumatarkkuus: kuinka moni näytetyistä päätyi myyjän listalle
  const osuvuus = (run: SearchRun): string =>
    run.found > 0 ? `${Math.round((run.imported / run.found) * 100)} %` : '—';

  return (
    <Osio>
      <h4>Liidihaut</h4>
      <ButtonRow>
        <Button onClick={lataa}>{auki ? 'Piilota haut' : 'Näytä haut'}</Button>
        {auki && !!runs?.length && (
          <Button onClick={() => {
            location.href = `/api/master/customers/${asiakas.id}/searches.xlsx`;
          }}>
            ⬇ Vie koulutusaineisto (Excel)
          </Button>
        )}
      </ButtonRow>

      {auki && (
        <>
          {!saatavilla && (
            <Warn>
              Asiakkaan kanta ei sisällä hakuhistoriaa. Se syntyy ensimmäisellä
              liidihaulla ohjelman päivityksen jälkeen.
            </Warn>
          )}
          {runs === null && saatavilla && <Empty>Ladataan…</Empty>}
          {runs?.length === 0 && saatavilla && <Empty>Asiakas ei ole tehnyt hakuja.</Empty>}
          {!!runs?.length && (
            <>
              <Hint style={{ marginTop: 8 }}>
                Sopivuus on 1–5: kuinka hyvin liidi vastaa asiakkaan yritysprofiilia.
                Paras on listan kärjessä. ”Otettu” kertoo, moniko tulos päätyi myyjän listalle.
              </Hint>
              <TableWrap>
                <Table $eiKlikattava>
                  <thead>
                    <tr>
                      <th>Aika</th><th>Hakuohje</th><th>Löytyi</th><th>Otettu</th>
                      <th>Hylätty</th><th>Verkkohakuja</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map(run => (
                      <tr key={run.id}>
                        <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                          {fmtDate(run.created_at)}
                          {run.user_name && <><br /><Muted>{run.user_name}</Muted></>}
                        </td>
                        <td style={{ fontSize: 13, maxWidth: 260 }}>
                          {run.instructions || <Muted>(ei ohjetta)</Muted>}
                          <br />
                          <Muted style={{ fontSize: 12 }}>
                            {run.country || 'Suomi'} · pyydetty {run.requested}
                            {run.min_revenue ? ` · lv ≥ ${liikevaihto(run.min_revenue)}` : ''}
                          </Muted>
                        </td>
                        <td>{run.found}</td>
                        <td><strong>{run.imported}</strong> <Muted>({osuvuus(run)})</Muted></td>
                        <td>{run.rejected || '—'}</td>
                        <td>{run.searches || '—'}</td>
                        <td style={{ textAlign: 'right' }}>
                          <Button $small onClick={() => void avaa(run.id)}>
                            {avattu === run.id ? 'Sulje' : 'Tulokset'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </>
          )}

          {avattu !== null && (
            <div style={{ marginTop: 10 }}>
              {rivit === null && <Empty>Ladataan tuloksia…</Empty>}
              {rivit?.length === 0 && <Empty>Ei tallennettuja tuloksia.</Empty>}
              {!!rivit?.length && (
                <TableWrap>
                  <Table $eiKlikattava>
                    <thead>
                      <tr>
                        <th>Yritys</th><th>Sopivuus</th><th>Y-tunnus</th><th>Paikkakunta</th>
                        <th>Liikevaihto</th><th>Rekisteri</th><th>Ratkaisu</th><th>Otettu</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rivit.map(r => (
                        <tr key={r.id}>
                          <td style={{ fontSize: 13 }}>
                            <strong>{r.company}</strong>
                            {r.official_name && r.official_name !== r.company && (
                              <><br /><Muted style={{ fontSize: 12 }}>{r.official_name}</Muted></>
                            )}
                          </td>
                          <td><Sopivuus arvo={r.fit} syy={r.fit_reason} /></td>
                          <td style={{ fontSize: 13 }}>{r.business_id || <Muted>—</Muted>}</td>
                          <td style={{ fontSize: 13 }}>{r.city || <Muted>—</Muted>}</td>
                          <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                            {liikevaihto(r.revenue_eur)}
                          </td>
                          <td style={{ fontSize: 12.5 }}>{r.register_status || <Muted>—</Muted>}</td>
                          <td style={{ fontSize: 12.5, maxWidth: 220 }}>
                            {r.verdict || <Muted>—</Muted>}
                          </td>
                          <td>
                            {r.imported
                              ? <NeutralChip>Kyllä</NeutralChip>
                              : <Muted>—</Muted>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
              )}
            </div>
          )}
        </>
      )}
    </Osio>
  );
}
